"use client";
import {
  Box,
  Divider,
  Flex,
  Heading,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Breadcrumbs } from "@areahub/ui/index";
import { ShareSocialMediaButtons } from "@areahub/ui/marketing/social-media/share-buttons";
import {
  GenericAuthor,
  PostAuthorList,
  PostMedia,
  PostMeta,
} from "@areahub/ui/post";
import { PostTableOfContents } from "@areahub/ui/post/table-of-contents/table-of-contents";
import dayjs from "dayjs";
import { Post } from "lib/gcms/gcms-typings";
import { usePathname } from "next/navigation";
import { Fragment } from "react";
import { getWebUrl } from "@areahub/config";

export interface PostWrapperProps {
  post: Post;
  children: React.ReactNode;
}

const PostWrapper = ({ post, children }: PostWrapperProps) => {
  const pathname = usePathname();
  const reportReference = pathname?.includes("reports")
    ? `/reports/${pathname.split("/").pop()}`
    : null;

  const headings = post.headings;
  const category = post.aggregatePostPage;
  const description = post?.excerpt;

  const tagHref = post?.aggregatePostPage
    ? `/individuals/knowledge-center/category/${post?.aggregatePostPage?.slug}`
    : undefined;

  const postMeta = `${dayjs(post?.published || "").format(
    "MMM D, YYYY",
  )} • Updated ${dayjs(post?.updatedAt || "").format("MMM D, YYYY")} • ${
    post?.readingTime
  } min read`;

  return (
    <Box maxW="1440px" mx="auto" px="5">
      <Stack as="article" flexDirection="row" justifyContent="center">
        {headings && (
          <PostTableOfContents
            visibility={headings.length === 0 ? "hidden" : "initial"}
            justifySelf="right"
            pt="6.3rem"
            display={{ base: "none", lg: "block" }}
            {...{ headings }}
          >
            <Fragment>
              <Text
                as="h2"
                textTransform="uppercase"
                fontWeight="bold"
                fontSize="xs"
                color={"gray.700"}
                letterSpacing="wide"
                my="4"
              >
                Share
              </Text>
              <ShareSocialMediaButtons
                justifyContent="flex-start"
                iconSx={{
                  size: "xs",
                }}
                shareUrl={`${getWebUrl()}${pathname}`}
                shareHashtag="AreaHub"
                shareTitle={post?.title}
                summary={description || ""}
              />
            </Fragment>
          </PostTableOfContents>
        )}
        <Box
          maxW="container.md"
          mx="auto"
          px={{ base: "6", md: "8" }}
          alignSelf="flex-start"
          mt="0"
        >
          {" "}
          <Box pt="10" pb={5}>
            <Breadcrumbs
              mb="5"
              breadcrumbs={
                reportReference
                  ? [
                      {
                        children: "Report",
                        href: reportReference,
                      },
                      {
                        children: post?.title,
                        isCurrentPage: true,
                        isLastChild: true,
                      },
                    ]
                  : [
                      {
                        children: "Knowledge Center",
                        href: "/individuals/knowledge-center",
                      },
                      {
                        children: category?.title,
                        href: `/individuals/knowledge-center/category/${category?.title?.toLowerCase()}`,
                      },
                      {
                        children: post?.title,
                      },
                    ]
              }
            />
            {/* title and short excerpt */}
            <Heading
              as="h1"
              size="2xl"
              mb="4"
              fontWeight="bold"
              letterSpacing="unset"
            >
              {post?.title}
            </Heading>
            <Text
              as="h2"
              size="md"
              fontWeight="medium"
              color="gray"
              lineHeight="tall"
              mb="4"
            >
              {post?.excerpt}
            </Text>
            <Divider />
            {/* author and tag information */}
            <Flex wrap="wrap-reverse" my="3">
              {post?.author && (
                <>
                  <PostAuthorList
                    authors={[post?.author as GenericAuthor]}
                    meta={postMeta}
                  />
                  <Spacer />
                </>
              )}
              <PostMeta
                type={category?.title || "Other"}
                url={tagHref}
                pb="2"
              />
            </Flex>
            {/* the main article image */}
            {post?.coverImage && post?.title && (
              <PostMedia
                src={post?.coverImage?.url}
                alt={post?.title}
                image={post?.coverImagePlaceholder}
                imageHeight={320}
                imageWidth={700}
              />
            )}
          </Box>
          {children}
        </Box>
      </Stack>
    </Box>
  );
};

export default PostWrapper;
